import { InlineEditableMultiSelect } from "../InlineEditableMultiSelect";
import { InlineEditableText } from "../InlineEditableText";
import {
  CONTROL_EXCEPTION_DESCRIPTIONS,
  PERFORMANCE_ISSUE_DESCRIPTIONS,
} from "../../../../../models/audit/attributeTestColumn";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../../../components/ui/accordion";
import { type AuditSession } from "../../../../../models/audit/auditSession";
import { useAttributeTestAnalysisContext } from "./AttributeTestAnalysisContext";

interface IssueDetailsSectionProps {
  isConclusive: boolean;
  attributeTestResult: AuditSession["sourceRows"][number]["attributeTestResults"][number];
  outcome: string | null;
  onUpdate: (
    field: string,
    value: string | number | object | boolean | null,
  ) => Promise<void>;
  disabled?: boolean;
}

export function IssueDetailsSection({
  isConclusive,
  outcome,
  attributeTestResult,
  onUpdate,
  disabled = false,
}: IssueDetailsSectionProps) {
  const { refetchActivityLogs } = useAttributeTestAnalysisContext();

  const handleUpdate = async (
    field: string,
    value: string | number | object | boolean | null,
  ) => {
    await onUpdate(field, value);
    await refetchActivityLogs();
  };

  return (
    <div className="flex flex-col gap-4 px-4 py-6 bg-slate-100">
      {isConclusive && outcome === "fail" && (
        <div>
          <div className="text-sm text-font-secondary font-mono mb-2">
            Control Exceptions
          </div>
          <InlineEditableMultiSelect
            disabled={disabled}
            values={
              attributeTestResult.isConclusive
                ? attributeTestResult.controlExceptions
                : []
            }
            options={Object.entries(CONTROL_EXCEPTION_DESCRIPTIONS).map(
              ([value, label]) => ({
                value,
                label,
              }),
            )}
            onSave={async (values) => {
              await handleUpdate("control_exceptions", values);
            }}
            emptyMessage="No control exceptions"
          />
        </div>
      )}

      {!isConclusive &&
        !attributeTestResult.isConclusive &&
        attributeTestResult.performanceIssues.length > 0 && (
          <div>
            <div className="text-sm text-font-secondary font-mono mb-2">
              Performance Issues
            </div>
            <InlineEditableMultiSelect
              disabled={disabled}
              values={attributeTestResult.performanceIssues}
              options={Object.entries(PERFORMANCE_ISSUE_DESCRIPTIONS).map(
                ([value, label]) => ({
                  value,
                  label,
                }),
              )}
              onSave={async (values) => {
                await handleUpdate("performance_issues", values);
              }}
              emptyMessage="No performance issues"
            />
          </div>
        )}

      <div>
        <div className="text-sm text-font-secondary font-mono mb-2">
          Conclusion
        </div>
        <InlineEditableText
          disabled={disabled}
          value={
            attributeTestResult.isConclusive
              ? (attributeTestResult.outcomeDescription ?? "")
              : attributeTestResult.partialDescription
          }
          onSave={async (value) => {
            await handleUpdate(
              isConclusive ? "outcome_description" : "partial_description",
              value,
            );
          }}
          placeholder="Enter conclusion..."
          className="!bg-slate-100"
        />
      </div>

      <div>
        <div className="text-sm text-font-secondary font-mono mb-2">
          Evidence Summary
        </div>
        <InlineEditableText
          disabled={disabled}
          value={attributeTestResult.evidenceSummary || ""}
          onSave={async (value) => {
            await handleUpdate("evidence_summary", value);
          }}
          placeholder="Enter evidence summary..."
          className="!bg-slate-100"
        />
      </div>

      {attributeTestResult.reperformanceSteps.length > 0 && (
        <div>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="steps-taken">
              <AccordionTrigger className="text-sm text-font-secondary font-mono">
                Steps Taken
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4">
                  {attributeTestResult.reperformanceSteps.map((step, index) => (
                    <div key={step.id || index}>
                      <div className="font-medium text-sm mb-1">
                        {index + 1}. {step.stepName}
                      </div>
                      <div className="text-sm text-font-secondary">
                        {step.stepDescription}
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}
    </div>
  );
}
